import type {
    Fleet,
    Planet,
    TickCheckpointEvent,
    TickCheckpointEventOf,
} from '@kaeliux/shared';
import { pendingBuilds } from './buildQueue';

/**
 * When a queued item's effect actually lands, as a timestamp — the single
 * source of truth for "is this done yet?", shared by the app-wide game loop
 * (`useGameLoop`) and the pages that render countdowns for the same queues.
 *
 * Each helper returns EVERY unresolved due timestamp in its queue, ascending.
 * The loop treats that list as the domain's identity: the earliest entry says
 * when to refresh, and any change to the list means the refresh made progress.
 * Cancelled events never count.
 */

function ascending(times: number[]): number[] {
    return [...times].sort((a, b) => a - b);
}

function pending<T extends TickCheckpointEvent['eventType']>(
    events: TickCheckpointEvent[],
    eventType: T,
): TickCheckpointEventOf<T>[] {
    return events.filter(
        (e): e is TickCheckpointEventOf<T> => e.eventType === eventType && !e.cancelled,
    );
}

/**
 * A held-delivery batch (Robots' grouped ship builds, issue #91; Recycling
 * Plant batches, issue #119) resolves at `deliverAt`, not `endDate` —
 * construction/processing finishes at `endDate` but the goods don't land until
 * the hold expires. Refreshing between the two is a guaranteed no-op, so
 * keying off `endDate` alone would poll forever in that window. An absent
 * `deliverAt` means "no hold": fall back to `endDate`.
 */
export function deliverAtMs(
    event: TickCheckpointEventOf<'shipBuild'> | TickCheckpointEventOf<'scrapRecycle'>,
): number {
    return (event.eventDetails.deliverAt ?? event.endDate).getTime();
}

export function buildingBuildDueTimesMs(planet: Planet | null | undefined): number[] {
    return ascending(pendingBuilds(planet).map((e) => e.endDate.getTime()));
}

export function shipBuildDueTimesMs(planet: Planet | null | undefined): number[] {
    return ascending(pending(planet?.tickCheckpoints ?? [], 'shipBuild').map(deliverAtMs));
}

export function scrapRecycleDueTimesMs(planet: Planet | null | undefined): number[] {
    return ascending(pending(planet?.tickCheckpoints ?? [], 'scrapRecycle').map(deliverAtMs));
}

/**
 * Every planet-side due timestamp in one list, because all three queues resolve
 * through the same `/tickPlanet` call — watching them separately would fire
 * concurrent, redundant ticks for a planet with more than one kind of batch
 * pending.
 */
export function planetDueTimesMs(planet: Planet | null | undefined): number[] {
    return ascending([
        ...buildingBuildDueTimesMs(planet),
        ...shipBuildDueTimesMs(planet),
        ...scrapRecycleDueTimesMs(planet),
    ]);
}

export function researchDueTimesMs(events: TickCheckpointEvent[]): number[] {
    return ascending(pending(events, 'scienceResearch').map((e) => e.endDate.getTime()));
}

export function shipUpgradeDueTimesMs(events: TickCheckpointEvent[]): number[] {
    return ascending(pending(events, 'shipUpgrade').map((e) => e.endDate.getTime()));
}

/**
 * An outbound fleet's next checkpoint is its arrival; anything else (at
 * target, returning) resolves when it lands.
 */
export function fleetDueTimesMs(fleets: Fleet[] | null | undefined): number[] {
    return ascending(
        (fleets ?? []).map((f) =>
            (f.status === 'outbound' ? f.arrivesAt : f.returnsAt).getTime(),
        ),
    );
}

/** The soonest of a due-time list, or null when nothing is queued. */
export function earliestDueMs(times: number[]): number | null {
    return times.length === 0 ? null : Math.min(...times);
}
